import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getGoogleWorkspaceSheets } from '../api';
import {
  loadGoogleApiScript,
  parseGoogleWorkspaceError,
} from '../utils/googleWorkspaceUtils';

export function GoogleSheetsIcon({ className = 'w-5 h-5 shrink-0' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path
        fill="#34A853"
        d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 14H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2z"
      />
    </svg>
  );
}

export function GoogleDriveIcon({ className = 'w-4 h-4 shrink-0' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#FBBC05" d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z" />
      <path fill="#4285F4" d="M22.85 15L16.29 3.5H9.15L15.71 15z" />
      <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
    </svg>
  );
}

/**
 * GoogleSheetSelector
 *
 * Supports selecting a Google Spreadsheet via:
 * 1. Google Picker Modal dialog (Option B)
 * 2. Backend Drive Sheets list dropdown (Option A / direct fallback)
 *
 * @param {{
 *   companyId: string|number,
 *   selectedSheet: { id: string, name: string, url?: string } | null,
 *   onSelectSheet: (sheet: { id: string, name: string, url?: string } | null) => void,
 *   disabled?: boolean,
 *   label?: string,
 *   helperText?: string,
 * }} props
 */
export function GoogleSheetSelector({
  companyId,
  selectedSheet,
  onSelectSheet,
  disabled = false,
  label = 'Select Google Spreadsheet',
  helperText,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPickerLoading, setIsPickerLoading] = useState(false);

  // 1. Fetch Google Drive Sheets from backend
  const {
    data: sheetsData,
    isLoading: isSheetsLoading,
    isError: isSheetsError,
    error: sheetsError,
    refetch: refetchSheets,
    isFetching: isRefetching,
  } = useQuery({
    queryKey: ['google-workspace-sheets', companyId],
    queryFn: () => getGoogleWorkspaceSheets(companyId),
    enabled: !!companyId,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const files = sheetsData?.files || [];

  const filteredFiles = files.filter((f) =>
    (f.name || '').toLowerCase().includes(searchTerm.toLowerCase().trim()),
  );

  // 2. Google Picker Modal Launch (Option B)
  const openGooglePicker = async () => {
    if (disabled) return;
    setIsPickerLoading(true);

    try {
      await loadGoogleApiScript();

      if (!window.gapi) {
        throw new Error('Google API client is unavailable.');
      }

      window.gapi.load('picker', {
        callback: () => {
          try {
            if (!window.google?.picker) {
              throw new Error('Google Picker library failed to initialize.');
            }

            const apiKey =
              (typeof import.meta !== 'undefined' &&
                (import.meta.env?.VITE_GOOGLE_API_KEY ||
                  import.meta.env?.VITE_GOOGLE_DEVELOPER_KEY)) ||
              '';

            const appId =
              (typeof import.meta !== 'undefined' &&
                (import.meta.env?.VITE_GOOGLE_APP_ID ||
                  import.meta.env?.VITE_GOOGLE_CLIENT_ID)) ||
              '';

            const view = new window.google.picker.View(
              window.google.picker.ViewId.SPREADSHEETS,
            );

            let builder = new window.google.picker.PickerBuilder()
              .addView(view)
              .setTitle('Select a Google Spreadsheet')
              .setCallback((data) => {
                if (
                  data[window.google.picker.Response.ACTION] ===
                  window.google.picker.Action.PICKED
                ) {
                  const doc = data[window.google.picker.Response.DOCUMENTS][0];
                  const pickedId = doc[window.google.picker.Document.ID];
                  const pickedName = doc[window.google.picker.Document.NAME];
                  const pickedUrl = doc[window.google.picker.Document.URL];

                  onSelectSheet({
                    id: pickedId,
                    name: pickedName || 'Selected Spreadsheet',
                    url:
                      pickedUrl ||
                      `https://docs.google.com/spreadsheets/d/${pickedId}`,
                  });
                  toast.success(`Selected "${pickedName}"`);
                }
              });

            if (apiKey) {
              builder = builder.setDeveloperKey(apiKey);
            }
            if (appId) {
              builder = builder.setAppId(appId);
            }

            const picker = builder.build();
            picker.setVisible(true);
            setIsPickerLoading(false);
          } catch (pickerErr) {
            console.warn(
              'Google Picker client dialog error, falling back to Drive file list:',
              pickerErr,
            );
            setIsPickerLoading(false);
            setIsDropdownOpen(true);
            toast.error(
              'Could not open Google Picker popup. You can select directly from the Drive list below.',
            );
          }
        },
      });
    } catch (err) {
      console.warn('Google Picker script error:', err);
      setIsPickerLoading(false);
      setIsDropdownOpen(true);
      toast.error(
        'Google Picker could not load. You can pick from your Drive files list below.',
      );
    }
  };

  const handleManualSelect = (file) => {
    onSelectSheet(file);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between">
        <label className="text-[13px] sm:text-[14px] font-semibold text-[#1F2241]">
          {label}
        </label>
        {selectedSheet && !disabled && (
          <button
            type="button"
            onClick={() => onSelectSheet(null)}
            className="text-[12px] font-medium text-red-500 hover:text-red-700 transition-colors"
          >
            Clear Selection
          </button>
        )}
      </div>

      {helperText && (
        <p className="text-[12px] text-gray-500 -mt-1 leading-relaxed">
          {helperText}
        </p>
      )}

      {/* When a sheet is already selected */}
      {selectedSheet ? (
        <div className="flex items-center justify-between p-3.5 rounded-xl border border-green-200 bg-green-50/60 shadow-sm transition-all">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-white border border-green-100 flex items-center justify-center shrink-0 shadow-xs">
              <GoogleSheetsIcon className="w-6 h-6 shrink-0" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[14px] font-semibold text-gray-900 truncate">
                {selectedSheet.name || 'Google Spreadsheet'}
              </span>
              <span className="text-[11px] text-gray-500 font-mono truncate">
                ID: {selectedSheet.id}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-3">
            {selectedSheet.url && (
              <a
                href={selectedSheet.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-green-700 bg-white border border-green-200 hover:bg-green-50 transition-colors"
                title="Open in Google Sheets"
              >
                <span>Open</span>
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={() => {
                  onSelectSheet(null);
                  setIsDropdownOpen(true);
                }}
                className="px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Change
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Selection Container */
        <div className="flex flex-col gap-2.5">
          {/* Action Row: Google Picker Modal Button + Browse Toggle */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={disabled || isPickerLoading}
              onClick={openGooglePicker}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-[13px] sm:text-[14px] font-medium text-gray-800 shadow-sm transition-all focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed group"
            >
              <GoogleDriveIcon className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
              <span>
                {isPickerLoading
                  ? 'Loading Google Picker...'
                  : 'Open Google Drive Picker'}
              </span>
            </button>

            <button
              type="button"
              disabled={disabled}
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 text-[13px] font-medium text-gray-700 transition-colors focus:outline-none"
            >
              <span>
                {isDropdownOpen ? 'Hide Drive List' : 'Browse Drive List'}
              </span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {/* Drive List Dropdown / Searchable panel */}
          {isDropdownOpen && (
            <div className="flex flex-col p-3 rounded-xl border border-gray-200 bg-white shadow-md animate-in fade-in slide-in-from-top-2 duration-150">
              <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-gray-100">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search spreadsheets in Drive..."
                    className="w-full pl-8 pr-3 py-1.5 text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:bg-white transition-all"
                  />
                  <svg
                    className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                <button
                  type="button"
                  onClick={() => refetchSheets()}
                  disabled={isRefetching}
                  className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                  title="Refresh Drive List"
                >
                  <svg
                    className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>

              {/* List Content */}
              <div className="max-h-52 overflow-y-auto custom-scrollbar mt-2 flex flex-col gap-1">
                {isSheetsLoading ? (
                  <div className="p-4 flex flex-col items-center justify-center gap-2 text-center text-gray-400">
                    <svg
                      className="w-5 h-5 animate-spin text-green-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="opacity-25"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="text-[12px]">
                      Loading spreadsheets from Drive...
                    </span>
                  </div>
                ) : isSheetsError ? (
                  <div className="p-4 flex flex-col items-center justify-center gap-2 text-center">
                    <span className="text-[12px] text-red-500 font-medium">
                      {parseGoogleWorkspaceError(sheetsError).message}
                    </span>
                    <button
                      type="button"
                      onClick={() => refetchSheets()}
                      className="text-[11px] font-semibold text-primary underline"
                    >
                      Retry Loading
                    </button>
                  </div>
                ) : filteredFiles.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-[13px]">
                    {files.length === 0
                      ? 'No spreadsheets found in your Google Drive.'
                      : 'No spreadsheets matched your search.'}
                  </div>
                ) : (
                  filteredFiles.map((file) => (
                    <button
                      key={file.id}
                      type="button"
                      onClick={() => handleManualSelect(file)}
                      className="flex items-center justify-between p-2.5 rounded-lg hover:bg-green-50/70 text-left transition-colors group"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <GoogleSheetsIcon className="w-5 h-5 shrink-0" />
                        <span className="text-[13px] font-medium text-gray-800 truncate group-hover:text-green-800">
                          {file.name}
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-green-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                        Select →
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GoogleSheetSelector;
